/* ── BRIEF 126 §3 — THE QUOTE OF THE DAY, ON TOP, IN BOTH ───────────────────
 *
 * [RULED] "It moves to the TOP of the bell, and the Daily Overview gains the
 * same one. ONE PRODUCER, ONE RENDERING, TWO SURFACES — do not build a second
 * quote block."
 *
 * The PRODUCER was already one and is untouched: `App.tsx`'s `todayQuote`
 * state, seeded from `lx:quoteToday`/`lx:quoteDate` and filled once a day from
 * `src/data/quotes.ts`'s `getFallbackQuote()`, which picks by day-of-year so
 * the same quote stands all day. Nothing about which quote it is lives here.
 *
 * What was NOT one was the rendering: the bell had a block at the BOTTOM of its
 * frame and the Daily Overview had nothing at all. Adding a second block to the
 * modal would have been the defect 125 §6 spent a commit removing one level up
 * — two renderings of one thing, free to drift. So this is the same move
 * `SignInLine` made in 81 §7, for the same reason, and with the same shape:
 * one component, one `variant` carrying the FRAME and nothing else.
 *
 * ── WHY THE TOP, AND WHAT IT DISPLACES ─────────────────────────────────────
 *
 * At the bottom of a 600px-max panel holding twenty rows, the quote is below
 * the fold and is read by nobody. §5 moves the sign-in line the other way for
 * the mirror-image reason: one is a pleasantry that opens the day, the other is
 * a fact you check. Both surfaces now read the same way down.
 */
import { FS } from "../lib/tokens";

/** The shape `getFallbackQuote()` returns. Narrow on purpose: `todayQuote` is
 *  `useState<any>` in App.tsx and a new `any` here would be a new `any`. */
export type DayQuoteValue = { text?: string | null; author?: string | null } | null;

export default function DayQuote({
  quote,
  variant = "popup",
}: {
  quote: DayQuoteValue;
  /** `popup` — the row at the top of the bell dropdown, which has its own
   *  padding. `modal` — above the first section, on the modal's own padding. */
  variant?: "popup" | "modal";
}) {
  // Absent rather than empty: the day's quote is not fetched until the first
  // effect runs, and a bordered empty box in the meantime is worse than a
  // panel that gains a line a moment later.
  if (!quote || !quote.text) return null;
  return (
    <div
      style={
        variant === "modal"
          ? { padding: "0 0 10px", marginBottom: 10, borderBottom: "1px solid var(--br)" }
          : { padding: "12px 14px", borderBottom: "1px solid var(--br)" }
      }
    >
      <div
        style={{
          fontSize: FS.s13,
          fontStyle: "italic",
          color: "var(--ts)",
          lineHeight: 1.5,
        }}
      >
        “{quote.text}”
      </div>
      {quote.author && (
        <div
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            marginTop: 5,
            textAlign: "right",
          }}
        >
          — {quote.author}
        </div>
      )}
    </div>
  );
}
